import React, { useEffect, useRef, useState } from "react";
import { tvBoot, tvControlGet } from "../../lib/api";

const DEFAULT_CHECKS = [["NEURAL CORE", "ONLINE"], ["VOICE MATRIX", "ONLINE"], ["MARKET FEEDS", "LIVE"], ["BUSINESS LINKS", "SYNCED"], ["VISION · EYE", "ONLINE"], ["SECURITY GRID", "ARMED"]];
const TOTAL = 8000;

const Particles = ({ active }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!active) return;
    const cv = ref.current, ctx = cv.getContext("2d");
    const W = (cv.width = cv.offsetWidth), H = (cv.height = cv.offsetHeight);
    const ps = Array.from({ length: 520 }, () => {
      const a = Math.random() * Math.PI * 2, r = Math.max(W, H) * (0.5 + Math.random() * 0.5);
      const ta = Math.random() * Math.PI * 2, tr = Math.sqrt(Math.random()) * Math.min(W, H) * 0.16;
      return { x: W / 2 + Math.cos(a) * r, y: H / 2 + Math.sin(a) * r, tx: W / 2 + Math.cos(ta) * tr * 0.78, ty: H / 2 + Math.sin(ta) * tr };
    });
    let raf; const t0 = performance.now();
    const draw = (now) => {
      const k = Math.min(1, (now - t0) / 1800), e = 1 - Math.pow(1 - k, 3);
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = "#FFB020";
      for (const p of ps) { ctx.globalAlpha = 0.35 + e * 0.65; ctx.fillRect(p.x + (p.tx - p.x) * e, p.y + (p.ty - p.y) * e, 2.2, 2.2); }
      if (k < 1) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [active]);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full" />;
};

export const NexusBoot = ({ userId, onDone }) => {
  const [t, setT] = useState(0);
  const [boot, setBoot] = useState(null);
  const doneRef = useRef(false);
  const bootRef = useRef(null);
  const finish = () => { if (doneRef.current) return; doneRef.current = true; onDone(bootRef.current); };

  useEffect(() => { tvBoot(userId).then((b) => { bootRef.current = b; setBoot(b); }).catch(() => {}); }, [userId]);
  useEffect(() => {
    const t0 = Date.now();
    const iv = setInterval(() => { const e = Date.now() - t0; setT(e); if (e >= TOTAL) { clearInterval(iv); finish(); } }, 50);
    const sk = setInterval(() => tvControlGet(userId).then((c) => {
      if (c.skip_ts && c.skip_ts * 1000 > t0 - 2000) { clearInterval(iv); clearInterval(sk); finish(); }
    }).catch(() => {}), 1000);
    return () => { clearInterval(iv); clearInterval(sk); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checks = boot?.checks || DEFAULT_CHECKS;
  const lineW = Math.min(1, t / 900);
  const phase = t < 2200 ? "logo" : t < 6000 ? "rings" : "assemble";
  const fade = t > 7400 ? Math.max(0, 1 - (t - 7400) / 600) : 1;

  return (
    <div className="fixed inset-0 z-[100] bg-[#02040a] overflow-hidden flex items-center justify-center" style={{ opacity: fade }} data-testid="nexus-boot">
      <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(rgba(0,240,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,240,255,0.04) 1px, transparent 1px)", backgroundSize: "48px 48px" }} />

      {phase === "logo" && (
        <div className="relative flex flex-col items-center">
          <div className="h-[3px] rounded-full bg-[#00F0FF]" style={{ width: `${lineW * 70}vw`, boxShadow: "0 0 24px #00F0FF, 0 0 60px #00F0FF" }} />
          <p className="nx-orbitron font-black tracking-[0.6em] text-[#00F0FF] mt-8 transition-all duration-700"
            style={{ fontSize: "7rem", opacity: t > 800 ? 1 : 0, letterSpacing: t > 800 ? "0.5em" : "1.4em", textShadow: "0 0 30px #00F0FF, 0 0 80px #00F0FF88" }}>NEXUS</p>
          <p className="nx-mono text-xl tracking-[0.8em] text-cyan-200/60 mt-2" style={{ opacity: t > 1400 ? 1 : 0, transition: "opacity .6s" }}>A V · NEURAL OS</p>
        </div>
      )}

      {phase === "rings" && (
        <div className="relative w-full h-full flex items-center justify-center">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="absolute rounded-full border-2" data-testid={`nexus-boot-ring-${i}`}
              style={{ width: `${26 + i * 12}vh`, height: `${26 + i * 12}vh`, borderColor: i % 2 ? "#FFB02088" : "#00F0FF99",
                borderStyle: i === 2 ? "dashed" : "solid", borderTopColor: "transparent",
                opacity: t - 2200 > i * 350 ? 1 : 0, transform: `rotate(${(t / (8 + i * 4)) * (i % 2 ? -1 : 1)}deg) scale(${t - 2200 > i * 350 ? 1 : 0.6})`,
                transition: "opacity .5s, transform .1s linear", boxShadow: `0 0 30px ${i % 2 ? "#FFB02033" : "#00F0FF33"}` }} />
          ))}
          <div className="absolute w-[16vh] h-[16vh] rounded-full" style={{ background: "radial-gradient(circle, #FFB020 0%, #FFB02055 35%, transparent 70%)", opacity: Math.min(1, (t - 2600) / 1500) }} />
          <div className="absolute left-[8vw] top-1/2 -translate-y-1/2 space-y-4" data-testid="nexus-boot-checks">
            {checks.map(([k, v], i) => {
              const on = t - 2600 > i * 500;
              return (
                <div key={k} className="nx-mono text-2xl tracking-widest flex gap-6 transition-opacity duration-300" style={{ opacity: on ? 1 : 0 }}>
                  <span className="text-cyan-200/70 w-72">{k}</span>
                  <span className="text-cyan-300/40">··········</span>
                  <span className={t - 2600 > i * 500 + 300 ? "text-emerald-300" : "text-amber-300 animate-pulse"}>{t - 2600 > i * 500 + 300 ? v : "SYNC"}</span>
                </div>
              );
            })}
          </div>
          <p className="absolute bottom-[10vh] nx-mono text-xl tracking-[0.6em] text-[#00F0FF]">POWERING UP · {Math.min(100, Math.round(((t - 2200) / 3800) * 100))}%</p>
        </div>
      )}

      {phase === "assemble" && (
        <>
          <Particles active />
          <p className="absolute bottom-[10vh] nx-orbitron text-4xl font-black tracking-[0.4em] text-[#FFB020]" style={{ textShadow: "0 0 24px #FFB020" }}>ALL SYSTEMS ONLINE</p>
        </>
      )}

      <p className="absolute top-6 right-8 nx-mono text-sm tracking-widest text-cyan-300/40">BOOT SEQ · {(t / 1000).toFixed(1)}s</p>
    </div>
  );
};
